/**
 * UMBRAL-01 — Tolerancias posicionales para infraestructuras PTEL
 *
 * Fundamento normativo (INVEST-01, 2026-03-27):
 *   - Decreto 69/2024 (Junta de Andalucía): no fija umbral métrico explícito.
 *   - RD 1071/2007: obliga a usar ETRS89; sin umbral para datos de usuario.
 *   - ISO 19157: metodología de medición; sin umbral universal.
 *   - INSPIRE AD — RMSE ≤ 5 m (urbano), ≤ 50 m (rural) → referencia técnica de facto.
 *   - INSPIRE BU — RMSE ≤ 3 m (cartografía 1:5.000).
 *   - BCA10/IECA — EMC ≤ 2,5 m (base cartográfica oficial de Andalucía).
 *   - Operacional GREA — ≤ 25 m para navegación de emergencia en vehículo.
 *
 * Los valores de UMBRAL_POSICIONAL son el resultado de sintetizar
 * los umbrales INSPIRE/BCA10 con los requisitos operacionales GREA.
 *
 * @module services/validation/UmbralCalidadPosicional
 * @version 1.0.0
 */

// ============================================================================
// CONSTANTES
// ============================================================================

/**
 * Umbrales métricos (RMSE estimado en metros) para las tres bandas de calidad.
 *
 * Límite ALTA/MEDIA = 10 m:
 *   Precisión típica de CartoCiudad/CDAU; identifica inequívocamente el edificio
 *   a escala 1:5.000; compatible con navegación GPS a puerta de emergencia.
 *
 * Límite MEDIA/BAJA = 50 m:
 *   Máximo INSPIRE-AD para zonas rurales; por encima puede confundirse con
 *   el inmueble colindante o la acera opuesta.
 *
 * Inútil operacional = 200 m:
 *   Centroide de núcleo urbano en municipios pequeños; inaceptable para
 *   despacho de emergencias independientemente del score.
 */
export const UMBRAL_POSICIONAL = {
  /** Límite superior banda ALTA (m) */
  ALTA_MAX_M: 10,
  /** Límite superior banda MEDIA (m) */
  MEDIA_MAX_M: 50,
  /**
   * Por encima de este valor (m) la coordenada se considera inútil
   * para operaciones de emergencia. Marcar como SIN_GEOLOCALIZAR
   * independientemente del score de calidad.
   */
  INUTIL_OPERACIONAL_M: 200,
} as const;

/**
 * Umbrales de score (0–100) asociados a cada banda.
 * Se exige cumplimiento simultáneo de RMSE y score para clasificar ALTA.
 */
export const SCORE_UMBRAL = {
  /** Score mínimo para clasificar ALTA */
  ALTA_MIN: 80,
  /** Score mínimo para clasificar MEDIA */
  MEDIA_MIN: 50,
} as const;

// ============================================================================
// TIPOS
// ============================================================================

/**
 * Banda de calidad posicional de una infraestructura PTEL.
 *
 * - ALTA:  posición confirmada, uso operacional directo sin revisión.
 * - MEDIA: posición aproximada, revisión técnica recomendada.
 * - BAJA:  posición insuficiente, requiere corrección manual.
 */
export type BandaCalidadPosicional = 'ALTA' | 'MEDIA' | 'BAJA';

/**
 * Resultado de la clasificación posicional de un registro.
 */
export interface ResultadoBandaPosicional {
  banda: BandaCalidadPosicional;
  /** RMSE estimado en metros usado para la clasificación */
  rmseEstimadoM: number;
  /** Score de calidad (0–100) usado para la clasificación */
  score: number;
  /**
   * true cuando rmseEstimadoM > UMBRAL_POSICIONAL.INUTIL_OPERACIONAL_M.
   * En ese caso el registro debe marcarse como SIN_GEOLOCALIZAR aunque
   * tenga un score aceptable (p. ej. centroide municipal geocodificado).
   */
  inutilOperacional: boolean;
  /** Descripción breve legible para mostrar en UI o exportación */
  descripcion: string;
}

// ============================================================================
// RMSE ESTIMADO POR FUENTE
// ============================================================================

/**
 * RMSE estimado en metros por fuente de geocodificación.
 *
 * Valores derivados de benchmarks publicados de CartoCiudad/CDAU y de la
 * observación empírica del proyecto (PLAN_MAESTRO v6.1, INVEST-01).
 * Usar como entrada a clasificarBandaPosicional cuando no se dispone
 * de RMSE medido directamente.
 *
 * Pendiente: sustituir por valores empíricos del benchmark BM-03.
 */
export const RMSE_POR_FUENTE: Record<string, number> = {
  /** Coordenada UTM30 original con ≥ 2 decimales */
  'original-utm30': 1,
  /** CartoCiudad con portal y número de vía */
  'cartociudad-numero': 6,
  /** CDAU con número de vía */
  'cdau-numero': 8,
  /** CartoCiudad solo calle, sin número */
  'cartociudad-calle': 30,
  /** CDAU solo calle, sin número */
  'cdau-calle': 35,
  /** DERA WFS — centroide de polígono de equipamiento */
  'dera-wfs-centroide': 20,
  /** Catastro por referencia catastral */
  'catastro-rc': 10,
  /** Nominatim OSM con número */
  'nominatim-numero': 25,
  /** Nominatim OSM sin número (municipio o calle) */
  'nominatim-municipio': 300,
  /** Centroide municipal (fallback INE) */
  'centroide-municipal': 1500,
};

// ============================================================================
// FUNCIÓN PRINCIPAL
// ============================================================================

/**
 * Clasifica la calidad posicional de un registro en tres bandas.
 *
 * @param rmseEstimadoM - RMSE estimado en metros (usar RMSE_POR_FUENTE si no se dispone de valor medido)
 * @param score         - Score de calidad 0–100 del sistema de validación PTEL Tool
 * @returns ResultadoBandaPosicional con banda, flag inútil operacional y descripción
 *
 * @example
 * // Coordenada de CartoCiudad con número → banda ALTA
 * clasificarBandaPosicional(6, 90);
 * // → { banda: 'ALTA', rmseEstimadoM: 6, score: 90, inutilOperacional: false, descripcion: '...' }
 *
 * @example
 * // Centroide municipal → inútil operacional
 * clasificarBandaPosicional(1500, 30);
 * // → { banda: 'BAJA', ..., inutilOperacional: true }
 */
export function clasificarBandaPosicional(
  rmseEstimadoM: number,
  score: number
): ResultadoBandaPosicional {
  const inutilOperacional = rmseEstimadoM > UMBRAL_POSICIONAL.INUTIL_OPERACIONAL_M;

  let banda: BandaCalidadPosicional;
  let descripcion: string;

  if (
    !inutilOperacional &&
    rmseEstimadoM <= UMBRAL_POSICIONAL.ALTA_MAX_M &&
    score >= SCORE_UMBRAL.ALTA_MIN
  ) {
    banda = 'ALTA';
    descripcion = `Posición confirmada (RMSE ≤ ${UMBRAL_POSICIONAL.ALTA_MAX_M} m, score ${score})`;
  } else if (
    !inutilOperacional &&
    rmseEstimadoM <= UMBRAL_POSICIONAL.MEDIA_MAX_M &&
    score >= SCORE_UMBRAL.MEDIA_MIN
  ) {
    banda = 'MEDIA';
    descripcion = `Posición aproximada (RMSE ≤ ${UMBRAL_POSICIONAL.MEDIA_MAX_M} m, score ${score})`;
  } else {
    banda = 'BAJA';
    descripcion = inutilOperacional
      ? `Posición inútil operacionalmente (RMSE ${rmseEstimadoM} m > ${UMBRAL_POSICIONAL.INUTIL_OPERACIONAL_M} m)`
      : `Posición insuficiente (RMSE ${rmseEstimadoM} m, score ${score})`;
  }

  return { banda, rmseEstimadoM, score, inutilOperacional, descripcion };
}
